import { type HgSupportedLanguage } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { getIaziId } from '@/helpers/geo/geoMapping';
import { type LevelIdValue } from '@/models/iazi/common/enums';
import { getLocalizedPageRoute } from '@/router';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';
import { EventSource } from '@/types';
import { captureAction } from '@smg-real-estate/browser-rum';

interface PushLocationOnRouterContext {
  pushLocation: (from: EventSource, featureId?: LevelIdValue) => Promise<void>;
}
const useRouterLocationPush = (): PushLocationOnRouterContext => {
  const mapStore = useMapStore();
  const geoStore = useGeoStore();

  const { searchLocation, getUrlByGeoLocation } = storeToRefs(geoStore);
  const { currentLevel } = storeToRefs(mapStore);

  const router = useRouter();
  const route = useRoute();
  const { locale } = useI18n();

  const resetLocation = async (): Promise<void> => {
    await geoStore.setSearchLocation(null);
    if (route.params.location) {
      await router.push(route.path.replace(/\/[^/]+$/, ''));
    }
  };

  const pushBySpecificLocation = async (): Promise<void> => {
    if (searchLocation.value) {
      const language = locale.value as HgSupportedLanguage;
      await router.push(
        getLocalizedPageRoute(route, language, getUrlByGeoLocation.value[language]),
      );
    }
  };

  const pushLocationFromMap = async (featureId?: LevelIdValue): Promise<void> => {
    const iaziId = getIaziId(currentLevel.value, featureId);
    if (iaziId && featureId) {
      await geoStore.resolveSearchLocationById(iaziId);
      await pushBySpecificLocation();
    } else {
      captureAction('Invalid iazi-id when clicking on map', {
        featureId: featureId ?? '',
      });
    }
  };

  const pushLocation = async (from: EventSource, featureId?: LevelIdValue): Promise<void> => {
    mapStore.setEventSource(from);

    switch (from) {
      case EventSource.Map:
        await pushLocationFromMap(featureId);
        break;
      case EventSource.Search:
      case EventSource.Reload:
        await pushBySpecificLocation();
        break;
      case EventSource.Reset:
        await resetLocation();
        break;
      default:
        break;
    }
  };

  return { pushLocation };
};

export default useRouterLocationPush;
